import { Base64 } from 'js-base64';

import { parseJson } from '@/utils/string';

// Compact display metadata for a Google Workspace item, carried in the search
// deep-link (?meta=) so the snapshot explorer can highlight, pin, and restore the
// exact item even when it is not on the loaded list page. Token paging has no
// page jump, so we never seek to the item — we surface it from this blob instead.
export interface GWSDeepLinkItem {
  subject?: string;
  from?: string;
  to?: string;
  date?: string;
  size?: number;
  snapshotId?: string;
  // Drive mime type, so an off-page file's pinned row can resolve its Google
  // product icon; real Drive names carry no extension to fall back on.
  mimeType?: string;
  // Group deep-links carry the group's identity so the explorer can open the
  // group's sections view without paging through the groups list to find it.
  name?: string;
  email?: string;
}

// Persisted shape uses short keys to keep the URL small.
interface EncodedDeepLink {
  s?: string;
  f?: string;
  t?: string;
  d?: string;
  z?: number;
  sid?: string;
  m?: string;
  n?: string;
  e?: string;
}

export const encodeGWSDeepLink = (
  item: GWSDeepLinkItem
): string | undefined => {
  const shape: EncodedDeepLink = {
    s: item.subject,
    f: item.from,
    t: item.to,
    d: item.date,
    z: item.size,
    sid: item.snapshotId,
    m: item.mimeType,
    n: item.name,
    e: item.email,
  };
  const compact = Object.fromEntries(
    Object.entries(shape).filter(([, v]) => v !== undefined && v !== '')
  );
  // Nothing worth carrying (a result with no attributes) -> no param.
  return Object.keys(compact).length > 0
    ? Base64.encode(JSON.stringify(compact))
    : undefined;
};

export const decodeGWSDeepLink = (
  param: string | null | undefined
): GWSDeepLinkItem | null => {
  if (!param) {
    return null;
  }
  const shape = parseJson<EncodedDeepLink>(Base64.decode(param));
  if (!shape) {
    return null;
  }
  return {
    subject: shape.s,
    from: shape.f,
    to: shape.t,
    date: shape.d,
    size: shape.z,
    snapshotId: shape.sid,
    mimeType: shape.m,
    name: shape.n,
    email: shape.e,
  };
};
